<template>
  <d-container fluid class="main-content-container px-4">
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">{{ $t('marketing.salesTools') }}</span>
        <h3 class="page-title">{{ $t('marketing.newEmailMessage') }}</h3>
      </d-col>
      <d-col col sm="6" class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="secondary"
          @click.prevent="$router.push('/funkcje-sprzedazowe/wiadomosci-email')"
          class="mr-1">
          <i class="material-icons">arrow_back</i>
          {{ $t('ui.backToList') }}
        </d-button>
        <d-button
          theme="info"
          outline
          class="mr-2"
          @click.prevent="openTestPopup">
          <i class="material-icons">mail_lock</i>
          {{ $t('marketing.sendTestEmail') }}
        </d-button>
        <d-button
          theme="success"
          @click.prevent="validateForm">
          <i class="material-icons">add</i>
          {{ $t('marketing.addMessage') }}
        </d-button>
      </d-col>
    </d-row>

    <!-- Form -->
    <d-form :class="{ 'is-loading': !loadedSegments }">
      <validation-observer
        ref="mailing-new-form"
        tag="form"
        v-slot="{
          errors: formErrors,
          valid: isFormValid,
          validated: isFormValidated
        }">
        <d-card class="mb-5">
          <d-tabs
            v-sticky-tabs
            card
            :nav-class="loadedSegments ? validationResults : ''">

            <!-- Dane ogólne -->
            <d-tab
              :title="$t('ui.generalInfo')"
              active>
              <h4 class="mb-3 pb-3 border-bottom">
                {{ $t('ui.generalInfo') }}
              </h4>

              <d-row>
                <d-col cols="12">
                  <div class="form-group">
                    <label for="name">
                      {{ $t('ui.name') }}:
                    </label>

                    <validation-provider
                      rules="required"
                      name="name"
                      v-slot="{ errors }">
                      <d-input
                        :state="errors.length ? 'invalid' : null"
                        v-model="data.name" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>
                <d-col cols="12">
                  <div class="form-group">
                    <label for="subject">
                      {{ $t('marketing.emailSubject') }}:
                    </label>

                    <validation-provider
                      rules="required"
                      name="subject"
                      v-slot="{ errors }">
                      <d-input
                        :state="errors.length ? 'invalid' : null"
                        v-model="data.subject" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>
                <d-col cols="12">
                  <div class="form-group">
                    <label>
                      {{ $t('marketing.messageContent') }}:
                    </label>

                    <validation-provider
                      rules="required"
                      name="content"
                      v-slot="{ errors }">

                      <div :class="{ 'editor-is-invalid': errors.length }">
                        <wysiwyg-editor
                          v-model="data.content"
                          key="tinymce-editor"
                          :placeholder="$t('marketing.insertMessageContent')" />
                      </div>

                      <small class="form-text text-muted mt-2">
                        <i class="material-icons mr-1" style="font-size: 14px; vertical-align: middle; top: unset;">info</i>
                        {{ $t('marketing.availableVariablesInfo') }}:
                        <code class="px-1 bg-light border rounded mx-1">[customer_name]</code>
                        <code class="px-1 bg-light border rounded mx-1">[customer_email]</code>
                      </small>

                      <div v-if="errors.length" class="invalid-feedback d-block">
                        {{ errors[0] }}
                      </div>

                    </validation-provider>
                  </div>
                </d-col>
              </d-row>
            </d-tab>
            <!-- Segmenty -->
            <d-tab :title="$t('marketing.segments')">
              <h4 class="mb-3 pb-3 border-bottom">
                {{ $t('marketing.segments') }}
              </h4>
              <d-alert
                v-if="loadError"
                show
                theme="warning">
                {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
              </d-alert>
              <div
                v-else-if="!loadedSegments"
                class="pb-3 pt-3 text-center overlay-loading">
                {{ $t('ui.dataLoading') }}&hellip;
              </div>
              <validation-provider
                v-else
                rules="required"
                name="segments"
                v-slot="{ errors }">

                <div :class="{ 'table-is-invalid': errors.length }">
                  <mailing-segments-table
                    v-model="data.segments"
                    :segments="segmentsList" />
                </div>

                <div
                  v-if="errors.length"
                  class="invalid-feedback d-block mt-2">
                  {{ errors[0] }}
                </div>
              </validation-provider>
            </d-tab>

            <!-- Tłumaczenia -->
            <d-tab :title="$t('marketing.translations')">
              <p class="text-center">
                {{ $t('marketing.addingTranslationsAvailableAfterMailingSave') }}
              </p>
            </d-tab>
          </d-tabs>
        </d-card>
      </validation-observer>
    </d-form>
    <mailing-test-popup
      v-if="showTestPopup"
      :subject="data.subject"
      :content="data.content" />
  </d-container>
</template>

<script>
import Vue from 'vue';
import FormUtils from '@/utils/FormUtils.js';
import MailingSegmentsTable from './MailingSegmentsTable.vue';
import OrderWysiwyg from '@/components/orders/OrderWysiwyg.vue';
import MailingTestPopup from './MailingTestPopup.vue';

export default {
  name: 'marketing-mailing-new',
  components: {
    'wysiwyg-editor': OrderWysiwyg,
    'mailing-segments-table': MailingSegmentsTable,
    'mailing-test-popup': MailingTestPopup
  },
  computed: {
    validationResults () {
      if (!this.$refs['mailing-new-form']) {
        return '';
      }

      const errors = this.$refs['mailing-new-form'].errors;
      const errorKeys = Object.keys(errors).filter(key => errors[key].length > 0);
      const errorKeysString = errorKeys.join(',');
      const errorTabs = [];

      const mapping = [
        ['error-1', ['name', 'subject', 'content']],
        ['error-2', ['segments']]
      ];

      mapping.forEach(([className, fields]) => {
        if (fields.some(field => errorKeysString.includes(field))) {
          errorTabs.push(className);
        }
      });

      return errorTabs.join(' ');
    }
  },
  data () {
    return {
      data: {
        content: '',
        name: null,
        segments: [],
        subject: null
      },
      loadedSegments: false,
      loadError: false,
      segmentsList: [],
      selectAllSegments: false,
      selectedSegments: [],
      showTestPopup: false
    };
  },
  mounted () {
    this.loadAdditionalData();

    this.$bus.$on('mailing-test-popup-close', this.closeTestPopup);
  },
  methods: {
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/marketing/segments/items',
        loadedKey: 'loadedSegments',
        outputKey: 'segmentsList',
        errorKey: 'loadError',
      });
    },
    validateForm () {
      FormUtils.validate(this.$refs['mailing-new-form'], this.submitForm);
    },
    submitForm () {
      FormUtils.submit({
        $http: this.$http,
        endpoint: '/marketing/mailings/store',
        formData: this.data,
        successTitle: this.$t('marketing.addMailingSuccessTitle'),
        successText: this.$t('marketing.addMailingSuccessText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('marketing.addMailingErrorText'),
        successAction: (response) => {
          this.$router.push('/funkcje-sprzedazowe/wiadomosci-email/edycja/' + parseInt(response.id, 10));
        }
      });
    },
    toggleItems () {
      Vue.nextTick(() => {
        if (this.selectAllSegments) {
          for (let i = 0; i < this.segmentsList.length; i++) {
            let id = this.segmentsList[i];
            Vue.set(this.selectedSegments, id, true);
          }
        } else {
          Vue.set(this, 'selectedSegments', []);
        }
      });
    },
    openTestPopup () {
      this.showTestPopup = true;
    },
    closeTestPopup () {
      this.showTestPopup = false;
    }
  },
  beforeDestroy () {
    this.$bus.$off('mailing-test-popup-close', this.closeTestPopup);
  }
};
</script>

<style lang="scss" scoped>
form.is-loading {
  opacity: .25;
  pointer-events: none;
}
</style>
